import { useEffect, useState } from "react";
import { checklistsApi } from "../../services/checklistsApi";
import { useToast } from "../../context/ToastContext";
import { Card } from "../common/Card";
import { Button } from "../common/Button";
import { Badge } from "../common/Badge";
import { Field } from "../common/Field";
import { formatDateOnly } from "../../services/hiveActions";

/**
 * Checklists, on the hive (#228).
 *
 * The user: "The only way to access the checklists is by touching the area
 * around the weather app. Wouldn't it be better to have those available in
 * the hives."
 *
 * On web it was worse than that -- there was NO hive-level entry point at
 * all. Checklists lived behind a sidebar link, so running one meant leaving
 * the hive you were looking at and then remembering which hive you had
 * been in. A checklist is a per-hive activity; this puts it where the
 * activity happens.
 *
 * Per-item notes are the other half: "they should be able to leave a note
 * with each item and have it logged". Each note is optional and one click
 * away, never a field standing open demanding to be filled -- on mobile
 * this same flow is used with gloves on, and a required note per item
 * would make the feature slower than not using it.
 */
export function HiveChecklistCard({ hiveId }) {
  const { notify } = useToast();
  const [templates, setTemplates] = useState([]);
  const [defaultTemplate, setDefaultTemplate] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Active run: null when idle, otherwise { template, results }
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  async function load() {
    try {
      const [t, d, r] = await Promise.all([
        checklistsApi.listTemplates(),
        checklistsApi.defaultForHive(hiveId),
        checklistsApi.listRuns(hiveId),
      ]);
      setTemplates(t.templates || []);
      setDefaultTemplate(d.template || null);
      setRuns(r.runs || []);
    } catch {
      // Best-effort. This card sits among several on a busy page; a
      // checklist service that is briefly unhappy must not take the hive
      // view down with it.
      setTemplates([]);
    } finally {
      setLoaded(true);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [hiveId]);

  function start(template) {
    const full = templates.find((t) => t.template_id === template.template_id) || template;
    setActive({
      template: full,
      // Everything starts as Done. A walkthrough is mostly ticks, and
      // starting from "nothing checked" means the common path is the one
      // with the most clicks.
      results: (full.items || []).map((item_text) => ({ item_text, status: "Done", note: "" })),
    });
  }

  function setItem(index, patch) {
    setActive((a) => ({
      ...a,
      results: a.results.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    }));
  }

  async function finish() {
    setSaving(true);
    try {
      const r = await checklistsApi.recordRun(hiveId, {
        template_id: active.template.template_id,
        template_name: active.template.name,
        items: active.results.map(({ item_text, status, note }) => ({
          item_text,
          status,
          note: note.trim() || undefined,
        })),
      });
      notify(
        r.notes_left > 0
          ? `Logged ${r.items_done}/${r.items_total}, with ${r.notes_left} note${r.notes_left === 1 ? "" : "s"}`
          : `Logged ${r.items_done}/${r.items_total}`,
        "success",
      );
      setActive(null);
      load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't log that checklist", "danger");
    } finally {
      setSaving(false);
    }
  }

  if (!loaded) return null;

  // Nothing to offer and nothing to show. Rendering an empty card would
  // just be a heading taking up space on an already long page.
  if (templates.length === 0 && runs.length === 0) return null;

  if (active) {
    const done = active.results.filter((r) => r.status === "Done").length;
    return (
      <Card
        title={active.template.name}
        className="section-spacing"
        actions={<Button size="sm" variant="ghost" onClick={() => setActive(null)}>Cancel</Button>}
      >
        <p style={{ marginTop: 0, color: "var(--color-text-muted)" }}>
          {done} of {active.results.length} checked. Add a note to anything worth remembering.
        </p>
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "var(--space-3)" }}>
          {active.results.map((r, i) => (
            <li key={r.item_text} style={{ borderBottom: "1px solid var(--color-border)", paddingBottom: "var(--space-2)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flex: 1 }}>
                  <input
                    type="checkbox"
                    checked={r.status === "Done"}
                    onChange={(e) => setItem(i, { status: e.target.checked ? "Done" : "Skipped" })}
                  />
                  <span style={{ textDecoration: r.status === "Skipped" ? "line-through" : "none" }}>
                    {r.item_text}
                  </span>
                </label>
                {/* Skipped is a real outcome, not a failure to answer --
                    plenty of items do not apply on a given visit. */}
                {r.status === "Skipped" && <Badge tone="warning">Skipped</Badge>}
              </div>
              <div style={{ marginTop: "var(--space-2)" }}>
                <Field label={`Note on "${r.item_text}" (optional)`}>
                  {(id, aria) => (
                    <input id={id} {...aria} value={r.note}
                      onChange={(e) => setItem(i, { note: e.target.value })}
                      className="touch-target" />
                  )}
                </Field>
              </div>
            </li>
          ))}
        </ul>
        <Button onClick={finish} disabled={saving} style={{ marginTop: "var(--space-4)" }}>
          {saving ? "Saving…" : "Log this checklist"}
        </Button>
      </Card>
    );
  }

  return (
    <Card
      title="Checklists"
      className="section-spacing"
      actions={
        runs.length > 0 && (
          <Button size="sm" variant="ghost" onClick={() => setShowHistory((s) => !s)}>
            {showHistory ? "Hide history" : `History (${runs.length})`}
          </Button>
        )
      }
    >
      {defaultTemplate ? (
        <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
          <Button onClick={() => start(defaultTemplate)}>Start {defaultTemplate.name}</Button>
          {/* WHERE the default came from, not just that there is one
              (#288, "I don't understand the checklist assignments").
              A hive inheriting its yard's default used to call it "the
              default for this hive", so somebody who set one thing on a
              site could not work out why it followed them around. Naming
              the site teaches the relationship by showing it work. */}
          <span style={{ color: "var(--color-text-muted)" }}>
            {defaultTemplate.source === "Site" && defaultTemplate.source_label
              ? `the default for every hive at ${defaultTemplate.source_label}`
              : "the default for this hive"}
          </span>
        </div>
      ) : (
        <p style={{ marginTop: 0, color: "var(--color-text-muted)" }}>
          Pick one to run through now.
        </p>
      )}

      <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", marginTop: "var(--space-3)" }}>
        {templates
          .filter((t) => t.template_id !== defaultTemplate?.template_id)
          .map((t) => (
            <Button key={t.template_id} size="sm" variant="secondary" onClick={() => start(t)}>
              {t.name}
            </Button>
          ))}
      </div>

      {showHistory && (
        <ul style={{ listStyle: "none", margin: "var(--space-4) 0 0", padding: 0, display: "grid", gap: "var(--space-3)" }}>
          {runs.map((run) => (
            <li key={run.run_id} style={{ borderTop: "1px solid var(--color-border)", paddingTop: "var(--space-2)" }}>
              <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "baseline", flexWrap: "wrap" }}>
                <strong>{run.template_name}</strong>
                <span style={{ color: "var(--color-text-muted)" }}>
                  {formatDateOnly(run.started_date)} · {run.items_done}/{run.items_total}
                </span>
              </div>
              {/* Only items carrying a note, and only skipped ones. A wall
                  of fifteen ticks says nothing; what somebody wants from
                  history is what was unusual that day. */}
              <ul style={{ listStyle: "none", margin: "var(--space-1) 0 0", padding: 0 }}>
                {run.items
                  .filter((i) => i.note || i.status === "Skipped")
                  .map((i, n) => (
                    <li key={n} style={{ color: "var(--color-text-muted)" }}>
                      {i.status === "Skipped" ? "Skipped" : "✓"} {i.item_text}
                      {i.note ? ` — ${i.note}` : ""}
                    </li>
                  ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
